import React from 'react';

import { ReactComponent as BellIcon } from 'assets/images/ring.svg';

import { Divider, FormControlLabel, Switch } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { NotificationSettings } from 'types/User';
import { checkForPermission, Permission, usePermissions } from 'utils/permissions';

type FormEvent = { target: { name: string; value: boolean } };

interface Props {
  values: NotificationSettings;
  isSubmitting: boolean;
  handleSubmit: () => void;
  handleChange: (event: FormEvent) => void;
  status?: string;
  setStatus: (status: string) => void;
  dirty: boolean;
}

const FormContent: React.FC<Props> = (props) => {
  const { status, dirty, values } = props;

  const permissions = usePermissions();

  const onChange = (event: FormEvent) => {
    props.handleChange(event);
    props.setStatus('pending');
  };

  const onSwitchChange = (name: string) => (_: React.ChangeEvent, value: boolean) => {
    onChange({
      target: { value, name },
    });
  };

  const eventsEnabled = values.methodEmailAllowed;

  return (
    <form onSubmit={props.handleSubmit}>
      <div className="p-8 space-y-2 text-gray-600">
        <div className="flex text-gray-600 space-x-2 items-center">
          <BellIcon className="fill-gray-600" />
          <h4 className="font-bold">Filter event types</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          {checkForPermission([Permission.NotificationReturnLocationUpdates], permissions) && (
            <FormControlLabel
              className="fles-shrink-0"
              control={
                <Switch
                  checked={values.typeUpdateAllowed && eventsEnabled}
                  disabled={!eventsEnabled}
                  value="typeUpdateAllowed"
                  color="primary"
                  onChange={onSwitchChange('typeUpdateAllowed')}
                />
              }
              label={<h5>Return location updates</h5>}
            />
          )}
          {/* checkForPermission([Permission.NotificationImpossibleToReturn], permissions) && (
            <FormControlLabel
              className="fles-shrink-0"
              control={
                <Switch
                  checked={values.typeUnreturnableAllowed && eventsEnabled}
                  disabled={!eventsEnabled}
                  value="typeUnreturnableAllowed"
                  color="primary"
                  onChange={onSwitchChange('typeUnreturnableAllowed')}
                />
              }
              label={<h5>Impossible to return anywhere</h5>}
            />
          ) */}
          {checkForPermission([Permission.NotificationGateSchedule], permissions) && (
            <FormControlLabel
              className="fles-shrink-0"
              control={
                <Switch
                  checked={values.gateScheduleNotifications && eventsEnabled}
                  disabled={!eventsEnabled}
                  value="gateScheduleNotifications"
                  color="primary"
                  onChange={onSwitchChange('gateScheduleNotifications')}
                />
              }
              label={<h5>Gate Schedule</h5>}
            />
          )}
          {checkForPermission([Permission.NotificationAppointmentsRelease], permissions) && (
            <FormControlLabel
              className="fles-shrink-0"
              control={
                <Switch
                  checked={values.appointmentsReleaseNotifications && eventsEnabled}
                  disabled={!eventsEnabled}
                  value="appointmentsReleaseNotifications"
                  color="primary"
                  onChange={onSwitchChange('appointmentsReleaseNotifications')}
                />
              }
              label={<h5>Appointments release</h5>}
            />
          )}
          {checkForPermission([Permission.NotificationAppointmentsRelease], permissions) && (
            <FormControlLabel
              className="fles-shrink-0"
              control={
                <Switch
                  checked={values.typeHotContainersAllowed && eventsEnabled}
                  disabled={!eventsEnabled}
                  value="typeHotContainersAllowed"
                  color="primary"
                  onChange={onSwitchChange('typeHotContainersAllowed')}
                />
              }
              label={<h5>Receive daily container reports</h5>}
            />
          )}
        </div>
      </div>
      <Divider />
      <div className="p-8 space-y-2 text-gray-600">
        <div className="flex text-gray-600 space-x-2 items-center">
          <BellIcon className="fill-gray-600" />
          <h4 className="font-bold">Delivery method</h4>
        </div>
        <div className="flex">
          <FormControlLabel
            className="flex-1"
            control={
              <Switch
                checked={values.methodEmailAllowed}
                value="methodEmailAllowed"
                color="primary"
                onChange={onSwitchChange('methodEmailAllowed')}
              />
            }
            label={<h5>Get email notifications</h5>}
          />
        </div>
      </div>
      <Divider />
      <div className="flex items-center justify-end p-4 space-x-2">
        {dirty && status !== 'success' && (
          <h5 className="text-red-900">Do not forget to save your settings</h5>
        )}
        <LoadingButton
          loading={props.isSubmitting}
          type="submit"
          variant="contained"
          disabled={!dirty || status === 'success'}
        >
          {status === 'success' ? 'Success' : 'Save'}
        </LoadingButton>
      </div>
    </form>
  );
};
FormContent.displayName = 'NotificationSettings.FormContent';

export default FormContent;
